import type {FC, MouseEvent} from 'react';
import type {NativeSyntheticEvent, NativeTouchEvent} from 'react-native';

import type {EventPayloadAction, NormalizedEvent} from '../../../utils/events';
import type {WithDomStyleProp, WithTestID} from '../../../utils/utility-types';

import {Clickable} from '../../Clickable';
import {Icon} from '../../Icon';
import {useCheckboxV2Controller} from './Checkbox.v2.controller';
import * as S from './Checkbox.v2.styles';

export const CheckboxV2: FC<WithDomStyleProp<CheckboxV2.Props>> = ({
  checked: checkedProp,
  disabled,
  indeterminate,
  onClick,
  size = S.DEFAULT_SIZE,
  testID,
  variant = 'default',
  ...rest
}) => {
  const {checked, CheckSvg, clickHandler} = useCheckboxV2Controller({
    checked: checkedProp,
    disabled,
    indeterminate,
    onClick,
    variant,
    ...rest,
  });

  return (
    <Clickable onClick={clickHandler}>
      <S.Container
        checked={checked}
        data-test-id={testID}
        disabled={disabled}
        indeterminate={indeterminate}
        onClick={clickHandler}
        size={size}
        variant={variant}
        {...rest}
      >
        {checked || indeterminate ? (
          <Icon color={'icon.black'} size={size * 1.2} svg={CheckSvg} />
        ) : null}
      </S.Container>
    </Clickable>
  );
};

export namespace CheckboxV2 {
  export type ClickEventPayload = {
    checked: boolean;
  };
  export type Props = WithTestID & {
    /**
     * External state is checked flag.
     */
    checked?: boolean;
    /**
     * Should the checked prop be controlled by the component
     */
    controlled?: boolean;
    disabled?: boolean;
    /**
     * The indeterminate state
     */
    indeterminate?: boolean;
    /**
     * Click handler to run any additional logic.
     * @param action
     */
    onClick?: (
      action: EventPayloadAction<
        ClickEventPayload,
        NormalizedEvent<MouseEvent, NativeSyntheticEvent<NativeTouchEvent>>
      >,
    ) => any;
    /**
     * Size of the checkbox
     */
    size?: number;

    /**
     * Display variant.
     */
    variant?: Variant;
  };

  export type StyledProps = Omit<Props, 'onClick'>;

  export type Variant = 'circle' | 'default';
}
